// Fuzzy matching of cell VALUES onto customer entities - resolving "YELLOW CORN" in a
// spreadsheet to the site's Corn CommodityModel, "CBOT" to a counterparty, and so on.
//
// Distinct from ColumnMatchHelper, which matches column HEADERS onto model fields.
// Both were previously private to ChatAssistant.tsx, with weaker duplicates in
// MultiTabTradeFileParser.ts.

/** Below this score a match is not trusted and undefined is returned instead. */
export const MIN_ENTITY_MATCH_SCORE = 0.4;

/** Score how closely a search term matches a target name, on a [0, 1] scale. */
export function fuzzyMatchValue(searchTerm: unknown, target: string): number {
    if (searchTerm === undefined || searchTerm === null || searchTerm === '' || !target) return 0;

    const search = String(searchTerm).toLowerCase().trim();
    const targetLower = String(target).toLowerCase().trim();
    if (!search || !targetLower) return 0;

    // Exact match.
    if (search === targetLower) return 1.0;

    // One contains the other.
    if (targetLower.includes(search) || search.includes(targetLower)) return 0.8;

    // Word-based overlap.
    const searchWords = search.split(/[\s&,]+/).filter(w => w.length > 1);
    const targetWords = targetLower.split(/[\s&,]+/).filter(w => w.length > 1);

    let matchCount = 0;
    for (const searchWord of searchWords) {
        for (const targetWord of targetWords) {
            if (targetWord.includes(searchWord) || searchWord.includes(targetWord)) {
                matchCount++;
                break;
            }
        }
    }

    if (searchWords.length > 0) {
        const wordScore = matchCount / Math.max(searchWords.length, targetWords.length);
        if (wordScore > 0.5) return wordScore * 0.7;
    }

    // Weak prefix agreement.
    if (targetLower.startsWith(search.substring(0, 3)) || search.startsWith(targetLower.substring(0, 3))) {
        return 0.4;
    }

    return 0;
}

export interface EntityMatchResult<T> {
    item: T | undefined;
    score: number;
}

/** Best-matching item by one name field, or undefined below MIN_ENTITY_MATCH_SCORE. */
export function findBestEntityMatch<T extends Record<string, any>>(
    searchTerm: unknown,
    items: readonly T[] | undefined | null,
    nameField: string
): EntityMatchResult<T> {
    let bestMatch: T | undefined;
    let bestScore = 0;

    for (const item of items ?? []) {
        const score = fuzzyMatchValue(searchTerm, item[nameField] || '');
        if (score > bestScore) {
            bestScore = score;
            bestMatch = item;
            if (bestScore >= 1) break;
        }
    }

    return { item: bestScore >= MIN_ENTITY_MATCH_SCORE ? bestMatch : undefined, score: bestScore };
}

/**
 * Resolve a cell value to one of an enum's legal values, tolerating case and
 * punctuation differences ("BUY" -> Buy, "call" -> Call, "b/s" style inputs).
 * Returns undefined when nothing matches rather than guessing.
 */
export function matchEnumValue(
    value: unknown,
    enumValues: Record<string, string>
): string | undefined {
    if (value === undefined || value === null || value === '') return undefined;

    const normalized = String(value).toLowerCase().replace(/[\s_\-./]+/g, '');
    if (!normalized) return undefined;

    const legalValues = Object.values(enumValues);

    const exact = legalValues.find(v => String(v).toLowerCase().replace(/[\s_\-./]+/g, '') === normalized);
    if (exact !== undefined) return exact;

    // Single-letter shorthand common in trade files: B/S, C/P.
    if (normalized.length === 1) {
        const byInitial = legalValues.filter(v => String(v).toLowerCase().startsWith(normalized));
        if (byInitial.length === 1) return byInitial[0];
    }

    const byPrefix = legalValues.filter(v => {
        const candidate = String(v).toLowerCase().replace(/[\s_\-./]+/g, '');
        return candidate.startsWith(normalized) || normalized.startsWith(candidate);
    });
    return byPrefix.length === 1 ? byPrefix[0] : undefined;
}
